import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';

interface ForecastData {
  forecasts: Array<{
    month: string;
    forecast: number;
    confidence_lower: number;
    confidence_upper: number;
  }>;
  metadata: {
    accuracy_score: number;
    trend_direction: string;
  };
}

interface TeamMetrics {
  team_summary: {
    total_revenue: number;
    team_win_rate: number;
    avg_deal_size: number;
    team_size: number;
  };
}

interface ConversionFunnel {
  funnel_overview: {
    total_deals: number;
    overall_win_rate: number;
    total_pipeline_value: number;
  };
  stage_breakdown: Array<{
    stage: string;
    deals_count: number;
    deals_value: number;
    percentage_of_total: number;
  }>;
}

export default function AnalyticsPageMinimal() {
  const [selectedPeriod, setSelectedPeriod] = useState(90);
  const [activeTab, setActiveTab] = useState(0);

  const { data: forecastData, isLoading: forecastLoading } = useQuery<ForecastData>({
    queryKey: ['forecast', selectedPeriod],
    queryFn: () => baseClient.get('/analytics/sales-forecast?months_ahead=6'),
    retry: 1
  });

  const { data: teamMetrics, isLoading: teamLoading } = useQuery<TeamMetrics>({
    queryKey: ['team', selectedPeriod],
    queryFn: () => baseClient.get(`/analytics/team-performance?period_days=${selectedPeriod}`),
    retry: 1
  });

  const { data: conversionData, isLoading: conversionLoading } = useQuery<ConversionFunnel>({
    queryKey: ['conversion', selectedPeriod],
    queryFn: () => baseClient.get(`/advanced/conversion-funnel?period_days=${selectedPeriod}`),
    retry: 1
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (forecastLoading || teamLoading || conversionLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
          Loading analytics data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            <AnalyticsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            CRM Analytics Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Sales forecasting, performance metrics, and conversion tracking
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={selectedPeriod}
              label="Period"
              onChange={(e) => setSelectedPeriod(e.target.value as number)}
            >
              <MenuItem value={30}>30 Days</MenuItem>
              <MenuItem value={90}>90 Days</MenuItem>
              <MenuItem value={180}>180 Days</MenuItem>
              <MenuItem value={365}>1 Year</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Sales Forecasting" icon={<TimelineIcon />} />
          <Tab label="Team Performance" icon={<SpeedIcon />} />
          <Tab label="Conversion Analysis" icon={<AssessmentIcon />} />
        </Tabs>
      </Box>

      {/* Sales Forecasting Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Forecast Accuracy</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h3" color="primary">
                    {forecastData ? `${(forecastData.metadata.accuracy_score * 100).toFixed(0)}%` : 'N/A'}
                  </Typography>
                  <Chip
                    label={forecastData?.metadata.trend_direction || 'stable'}
                    color="default"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  AI model confidence in revenue predictions
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Revenue Forecasts</Typography>
                {forecastData && forecastData.forecasts.length > 0 ? (
                  <List>
                    {forecastData.forecasts.slice(0, 3).map((forecast, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${forecast.month}: ${formatCurrency(forecast.forecast)}`}
                          secondary={`Range: ${formatCurrency(forecast.confidence_lower)} - ${formatCurrency(forecast.confidence_upper)}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">No forecast data available</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Team Performance Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Total Revenue</Typography>
                <Typography variant="h4" color="primary">
                  {teamMetrics ? formatCurrency(teamMetrics.team_summary.total_revenue) : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Team Win Rate</Typography>
                <Typography variant="h4" color="success.main">
                  {teamMetrics ? `${teamMetrics.team_summary.team_win_rate}%` : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Avg Deal Size</Typography>
                <Typography variant="h4" color="info.main">
                  {teamMetrics ? formatCurrency(teamMetrics.team_summary.avg_deal_size) : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Team Size</Typography>
                <Typography variant="h4">
                  {teamMetrics ? teamMetrics.team_summary.team_size : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Performance Summary</Typography>
                <Alert severity="info">
                  Add more deals and assign team members to see detailed performance metrics
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Conversion Analysis Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Overall Win Rate</Typography>
                <Typography variant="h3" color="primary">
                  {conversionData ? `${conversionData.funnel_overview.overall_win_rate.toFixed(1)}%` : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {conversionData ? `${conversionData.funnel_overview.total_deals} total deals` : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Pipeline Value</Typography>
                <Typography variant="h3" color="success.main">
                  {conversionData ? formatCurrency(conversionData.funnel_overview.total_pipeline_value) : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Active Deals</Typography>
                <Typography variant="h3" color="info.main">
                  {conversionData ? conversionData.funnel_overview.total_deals : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Sales Funnel Breakdown</Typography>
                {conversionData && conversionData.stage_breakdown.length > 0 ? (
                  <List>
                    {conversionData.stage_breakdown.map((stage, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${stage.stage}: ${stage.deals_count} deals`}
                          secondary={`Value: ${formatCurrency(stage.deals_value)} (${stage.percentage_of_total.toFixed(1)}%)`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">No stage breakdown data available</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
